import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Camera, Clock, Shield, Award, CheckCircle, Phone, Image as ImageIcon } from 'lucide-react';

const BodyworkServiceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate process steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % processSteps.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const services = [
    {
      icon: '🔨',
      title: 'Réparation impacts',
      description: 'Remise en forme et retouche peinture',
      timeEstimate: '1-2 jours'
    },
    {
      icon: '✨',
      title: 'Rayures profondes',
      description: 'Ponçage, apprêt et peinture complète',
      timeEstimate: '1-2 jours'
    },
    {
      icon: '🔧',
      title: 'Débosselage',
      description: 'Remise en forme sans peinture (PDR)',
      timeEstimate: '1-3h'
    },
    {
      icon: '🎨',
      title: 'Peinture complète',
      description: 'Préparation et application multicouches',
      timeEstimate: '3-5 jours'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Envoi photos',
      description: 'Envoyez-nous des photos des dégâts',
      icon: Camera,
      color: 'from-blue-500 to-blue-600'
    },
    {
      step: 2,
      title: 'Devis gratuit',
      description: 'Estimation rapide et transparente',
      icon: ImageIcon,
      color: 'from-green-500 to-green-600'
    },
    {
      step: 3,
      title: 'Prise en charge',
      description: 'Récupération et réparation',
      icon: Shield,
      color: 'from-purple-500 to-purple-600'
    },
    {
      step: 4,
      title: 'Livraison',
      description: 'Véhicule comme neuf',
      icon: Award,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const advantages = [
    {
      icon: Camera,
      title: 'Estimation par photo',
      description: 'Devis gratuit à partir de photos'
    },
    {
      icon: Clock,
      title: 'Prise en charge rapide',
      description: 'Intervention dans les meilleurs délais'
    },
    {
      icon: Shield,
      title: 'Finitions soignées',
      description: 'Qualité professionnelle garantie'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 relative overflow-hidden"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-200/30 rounded-full animate-pulse" />
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-red-200/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Paint drops animation */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-paint-drop text-3xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${4 + Math.random() * 2}s`
              }}
            >
              🎨
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium mb-4">
            <span className="text-2xl mr-2">🎨</span>
            Atelier carrosserie - Bouskoura
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              Carrosserie
            </span>{' '}
            & Peinture
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" itemProp="description">
            Réparez les impacts, rayures ou bosses grâce à nos prestations de carrosserie. 
            Estimation gratuite à partir de photos, prise en charge rapide et finitions soignées.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* Content Side */}
          <div className={`space-y-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            
            {/* Process Steps */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Notre processus simplifié</h3>
              
              <div className="space-y-4">
                {processSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === activeStep;
                  
                  return (
                    <div
                      key={index}
                      className={`
                        relative flex items-center space-x-4 p-4 rounded-xl transition-all duration-500 cursor-pointer
                        ${isActive 
                          ? 'bg-gradient-to-r ' + step.color + ' text-white shadow-lg scale-105' 
                          : 'bg-white border border-gray-200 hover:border-orange-300 hover:shadow-md'
                        }
                      `}
                      onMouseEnter={() => setActiveStep(index)}
                    >
                      {/* Step number */}
                      <div className={`
                        flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg
                        ${isActive ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-600'}
                      `}>
                        {step.step}
                      </div>
                      
                      {/* Icon */}
                      <div className={`
                        flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                        ${isActive ? 'bg-white/20' : 'bg-orange-100'}
                      `}>
                        <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-orange-600'}`} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h4 className={`font-bold ${isActive ? 'text-white' : 'text-gray-900'}`}>
                          {step.title}
                        </h4>
                        <p className={`text-sm ${isActive ? 'text-white/90' : 'text-gray-600'}`}>
                          {step.description}
                        </p>
                      </div>
                      
                      {/* Progress indicator */}
                      {isActive && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Service Types */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Nos prestations carrosserie</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div 
                    key={index}
                    className={`
                      p-4 bg-white rounded-xl shadow-sm border border-gray-100
                      transition-all duration-500 hover:shadow-md hover:scale-105
                      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                    `}
                    style={{ transitionDelay: `${(index + 4) * 200}ms` }}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-3">{service.icon}</div>
                      <h5 className="font-semibold text-gray-900 mb-2">{service.title}</h5>
                      <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                      <div className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3 mr-1" />
                        {service.timeEstimate}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Advantages
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Nos avantages</h4>
              <div className="space-y-3">
                {advantages.map((advantage, index) => {
                  const Icon = advantage.icon;
                  return (
                    <div 
                      key={index}
                      className={`
                        flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100
                        transition-all duration-500 hover:shadow-md hover:scale-105
                        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
                      `}
                      style={{ transitionDelay: `${(index + 8) * 200}ms` }}
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{advantage.title}</h5>
                        <p className="text-sm text-gray-600">{advantage.description}</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </div> */}


          </div>

          {/* Visual Side */}
          <div className={`relative transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            
            {/* Main Workshop Image */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-orange-100 to-red-100 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/carosserie.png"
                  alt="Atelier de carrosserie automobile Faucon Bleu"
                  className="w-full h-96 object-cover"
                  loading="lazy"
                  itemProp="image"
                />
                
                {/* Overlay with info */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-4">Atelier carrosserie moderne</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">12m²</div>
                      <div className="text-sm opacity-90">Cabine peinture</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">48h</div>
                      <div className="text-sm opacity-90">Délai moyen</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">100%</div>
                      <div className="text-sm opacity-90">Satisfaction</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -left-4 bg-white rounded-xl p-4 shadow-lg border animate-float">
                <div className="text-center">
                  <div className="text-lg font-bold text-orange-600">Gratuit</div>
                  <div className="text-xs text-gray-600">Devis photo</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg border animate-float-delayed">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">Garantie</div>
                  <div className="text-xs text-gray-600">12 mois</div>
                </div>
              </div>

              <div className="absolute top-1/4 -right-6 bg-white rounded-xl p-3 shadow-lg border animate-float">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">Pro</div>
                  <div className="text-xs text-gray-600">Équipement</div>
                </div>
              </div>
            </div>

            {/* Before/After Gallery Teaser */}
              <h4 className="text-xl mt-8 font-bold text-gray-900">Nos accords sinistres</h4>

            <div className="mt-2 grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-center">
                 <img 
                 src='/images/SanlamAssurence.png'
                 />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-center">
                 <img 
                 src='/images/WafaAssurance.png'
                 />
                </div>
              </div>
            </div>

      
          </div>
        </div>
      </div>
            {/* CTA Buttons */}
              <div className={`flex flex-col sm:flex-row sm:space-x-[100px] space-y-10
              
              sm:space-y-0 transition-all duration-1000 justify-center items-center mt-8 delay-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}>
              <a
                href="https://wa.me/212608112626"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center
                 w-auto sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-600
                  to-red-600 text-white font-bold rounded-xl transition-all duration-300 transform 
                  hover:scale-125 scale-125 hover:shadow-xl"
              >
                <Camera className="w-5 h-5 mr-3" />
                <span>Envoyer des photos</span>
                <ArrowRight className="ml-3 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              
              <a
                href="tel:+212608112626"
                className="inline-flex items-center justify-center w-auto sm:w-auto
                 px-6 py-4 border-2 border-orange-600 text-orange-600 font-semibold scale-125
                 rounded-xl hover:bg-orange-600 hover:text-white transition-all duration-300"
              >
                <Phone className="w-5 h-5 mr-2" />
                Appeler pour devis
              </a>
            </div>
      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Carrosserie automobile - Atelier physique",
            "description": "Service de carrosserie automobile: réparation d'impacts, rayures, bosses. Estimation gratuite à partir de photos, prise en charge rapide et finitions soignées.",
            "provider": {
              "@type": "AutoRepair",
              "name": "Faucon Bleu",
              "url": "https://faucon-bleu.ma",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Bouskoura",
                "addressRegion": "Casablanca",
                "addressCountry": "MA"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "Casablanca",
              "addressCountry": "MA"
            },
            "serviceType": "Auto Body Repair",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services de carrosserie",
              "itemListElement": services.map((service, index) => ({
                "@type": "Offer",
                "position": index + 1,
                "itemOffered": {
                  "@type": "Service",
                  "name": service.title,
                  "description": service.description
                }
              }))
            },
            "offers": {
              "@type": "Offer",
              "name": "Estimation gratuite",
              "description": "Estimation gratuite à partir de photos",
              "price": "0",
              "priceCurrency": "MAD"
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-8px) rotate(2deg); }
          66% { transform: translateY(-4px) rotate(-2deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(1deg); }
        }
        
        @keyframes paint-drop {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.1; }
          50% { transform: translateY(-15px) scale(1.2); opacity: 0.3; }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite 1.5s;
        }
        
        .animate-paint-drop {
          animation: paint-drop 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default BodyworkServiceSection;